import { useRef, useState, useEffect } from "react";

import type {
    MonitorAndProtectModelCampaignsCampaignListItemModel,
    MonitorAndProtectModelSharedRequestDeviceType,
} from "@similarweb/pro-api-client";

import { useTranslation } from "pro-features/components/WithTranslation";
import { mnpNavigation } from "app/pages/digital-marketing/monitor-and-protect/app/navigation";
import { useCampaignWidgetsMetrics } from "app/pages/digital-marketing/monitor-and-protect/services/api/widgets";
import { CampaignMetricsWidgets } from "app/pages/digital-marketing/monitor-and-protect/shared/components/campaign-metrics-widgets";
import { MetricsWidgetCollection } from "app/pages/digital-marketing/monitor-and-protect/shared/components/metrics-widget-collection/metrics-widget-collection";
import CountryService from "app/services/CountryService";

interface ICampaignMetricsCardProps {
    campaign: MonitorAndProtectModelCampaignsCampaignListItemModel;
    sharedByUserName?: string;
    isOwner?: boolean;
    onShare: (link: string) => void;
    device?: MonitorAndProtectModelSharedRequestDeviceType;
}

export const CampaignMetricsCard = ({
    campaign,
    sharedByUserName,
    isOwner,
    onShare,
    device = "All",
}: ICampaignMetricsCardProps) => {
    const translate = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    const locations = campaign.locations.map((e) => ({
        country: e.countryCode,
        name: CountryService.getCountryById(e.countryCode)?.text,
    }));

    const countries = campaign.locations.map((e) => e.countryCode);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            {
                threshold: 0.1,
                rootMargin: "50px",
            },
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    const { data, isLoading } = useCampaignWidgetsMetrics(
        campaign.id,
        isVisible && !!campaign.id && countries.length > 0,
        countries,
        device,
    );

    return (
        <div ref={containerRef}>
            <MetricsWidgetCollection
                id={campaign.id}
                key={campaign.id}
                domain={campaign.domain}
                periodLabel={translate("monitor_and_protect.homepage.period_label")}
                campaignName={campaign.name}
                locations={locations}
                sharedBy={sharedByUserName}
                isOwner={isOwner}
                target={"_self"}
                href={mnpNavigation.overview.getLink({ campaignId: campaign.id }, { absolute: true })}
                type={campaign.type}
                onShare={(link) => onShare(link)}
                onSettings={() => void mnpNavigation.settings.go({ campaignId: campaign.id })}
            >
                <CampaignMetricsWidgets
                    data={data}
                    isLoading={isLoading || !isVisible}
                    domain={campaign.domain}
                    titleComponent="text"
                />
            </MetricsWidgetCollection>
        </div>
    );
};
